import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Step {
  id: number;
  title: string;
  description: string;
  completed: boolean;
  current: boolean;
}

interface ProgressStepsProps {
  steps: Step[];
}

const ProgressSteps: React.FC<ProgressStepsProps> = ({ steps }) => {
  return (
    <div className="py-4 sm:py-8">
      {/* Desktop View */}
      <nav aria-label="Progress" className="hidden sm:block">
        <ol className="flex items-center">
          {steps.map((step, stepIdx) => (
            <li
              key={step.id}
              className={`relative ${
                stepIdx !== steps.length - 1 ? 'pr-20' : ''
              }`}
            >
              {stepIdx !== steps.length - 1 && (
                <div
                  className="absolute inset-0 flex items-center"
                  aria-hidden="true"
                >
                  <div
                    className={`h-0.5 w-full ${
                      step.completed ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  />
                </div>
              )}
              
              <div className="relative flex items-center justify-center">
                <div
                  className={`h-8 w-8 rounded-full flex items-center justify-center ${
                    step.completed
                      ? 'bg-blue-600'
                      : step.current
                      ? 'bg-blue-600'
                      : 'bg-gray-200'
                  }`}
                >
                  {step.completed ? (
                    <Check className="h-5 w-5 text-white" />
                  ) : (
                    <span
                      className={`text-sm font-medium ${
                        step.current ? 'text-white' : 'text-gray-500'
                      }`}
                    >
                      {step.id}
                    </span>
                  )}
                </div>
              </div>
              
              <div className="mt-2 text-center">
                <div
                  className={`text-sm font-medium ${
                    step.current || step.completed
                      ? 'text-blue-600'
                      : 'text-gray-500'
                  }`}
                >
                  {step.title}
                </div>
                <div className="text-xs text-gray-500">{step.description}</div>
              </div>
            </li>
          ))}
        </ol>
      </nav>

      {/* Mobile View */}
      <nav aria-label="Progress" className="block sm:hidden">
        <ol className="space-y-4">
          {steps.map((step, stepIdx) => (
            <li key={step.id} className="relative">
              <div className="flex items-center">
                <div
                  className={cn(
                    "flex h-12 w-12 items-center justify-center rounded-full",
                    step.completed ? "bg-blue-600" : 
                    step.current ? "bg-blue-600" : 
                    "bg-gray-200"
                  )}
                >
                  {step.completed ? (
                    <Check className="h-6 w-6 text-white" />
                  ) : (
                    <span
                      className={cn(
                        "text-lg font-semibold",
                        step.current ? "text-white" : "text-gray-500"
                      )}
                    >
                      {step.id}
                    </span>
                  )}
                </div>
                <div className="ml-4 flex-1">
                  <div
                    className={cn(
                      "text-base font-semibold",
                      step.current || step.completed ? "text-blue-600" : "text-gray-500"
                    )}
                  >
                    {step.title}
                  </div>
                  <div className="text-sm text-gray-500">
                    {step.description}
                  </div>
                </div>
                {step.completed && (
                  <div className="ml-4">
                    <Check className="h-5 w-5 text-blue-600" />
                  </div>
                )}
              </div>
              {stepIdx !== steps.length - 1 && (
                <div
                  className={cn(
                    "absolute left-6 top-12 h-12 w-0.5",
                    step.completed ? "bg-blue-600" : "bg-gray-200"
                  )}
                  aria-hidden="true"
                />
              )}
            </li>
          ))}
        </ol>
      </nav>
    </div>
  );
};

export default ProgressSteps;
